import { getSimpleSeededRandomGenerator, getUserError } from '../../../components/miscUtils';

export class Color {
  r: number;
  g: number;
  b: number;
  a: number;

  constructor(r: number, g: number, b: number, a?: number) {
    this.r = r;
    this.g = g;
    this.b = b;
    this.a = a || 1;
  }

  opacity(fraction: number): Color {
    return color(this.r, this.g, this.b, fraction);
  }

  desaturate(fraction: number): Color {
    const av = (this.r + this.g + this.b) / 3;
    return color(
      av * fraction + this.r * (1 - fraction),
      av * fraction + this.g * (1 - fraction),
      av * fraction + this.b * (1 - fraction),
    );
  }

  darken(fraction: number): Color {
    return color(this.r * (1 - fraction), this.g * (1 - fraction), this.b * (1 - fraction));
  }

  lighten(fraction: number): Color {
    return color(
      255 * fraction + this.r * (1 - fraction),
      255 * fraction + this.g * (1 - fraction),
      255 * fraction + this.b * (1 - fraction),
    );
  }

  toCanvasString() {
    return `rgba(${this.r},${this.g},${this.b},${this.a})`;
  }
}

export function color(r: number, g: number, b: number, a?: number) {
  return new Color(r, g, b, a);
}

export function array2Color(rgbValues: number[]): Color {
  if (rgbValues.length < 3) throw `RGB array should containt at least 3 values`;
  return color(rgbValues[0], rgbValues[1], rgbValues[2], rgbValues[3] ?? 1);
}

export function hslColor(h: number, s: number, l: number) {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return color(255 * f(0), 255 * f(8), 255 * f(4));
}

export function interpolateColors(col1: Color, col2: Color, fr: number): Color {
  return color(
    col1.r * fr + col2.r * (1 - fr),
    col1.g * fr + col2.g * (1 - fr),
    col1.b * fr + col2.b * (1 - fr),
    col1.a * fr + col2.a * (1 - fr),
  );
}

export type TpDiscreteColorSet = {
  name: string;
  colors: Color[];
};

export type TpNumericalColorSet = {
  name: string;
  colors: Color[];
};

const discColorSet1 = {
  name: 'Default',
  colors: [
    color(240, 50, 120),
    color(63, 180, 255),
    color(78, 233, 143),
    color(230, 214, 0),
    color(154, 24, 245),
    color(248, 160, 0),
    color(220, 10, 180),
    color(180, 210, 255),
    color(60, 190, 160),
    color(255, 150, 150),
    color(0, 130, 200),
    color(20, 170, 30),
  ],
};

const discColorSet2 = {
  name: 'Basic',
  colors: [
    color(228, 26, 28),
    color(55, 126, 184),
    color(77, 175, 74),
    color(152, 78, 163),
    color(255, 127, 0),
    color(255, 255, 51),
    color(166, 86, 40),
    color(247, 129, 191),
  ],
};

const discColorSet3 = {
  name: 'Saturated',
  colors: [
    color(20, 160, 140),
    color(215, 90, 0),
    color(110, 110, 180),
    color(230, 40, 140),
    color(120, 165, 20),
    color(230, 170, 0),
    color(165, 115, 30),
    color(120, 120, 230),
    color(255, 120, 120),
  ],
};

const discColorSet4 = {
  name: 'Dark',
  colors: [
    color(220, 130, 0),
    color(184, 0, 90),
    color(0, 140, 250),
    color(0, 110, 0),
    color(0, 187, 170),
    color(210, 100, 230),
    color(178, 69, 0),
    color(255, 145, 135),
    color(90, 85, 215),
    color(0, 200, 250),
    color(135, 133, 0),
    color(0, 165, 110),
  ],
};

const discColorSet_20cols = {
  name: 'Default 20',
  colors: [
    color(114, 229, 239),
    color(8, 115, 148),
    color(135, 169, 253),
    color(78, 59, 126),
    color(216, 94, 225),
    color(138, 35, 139),
    color(127, 235, 144),
    color(9, 96, 19),
    color(181, 208, 141),
    color(12, 168, 46),
    color(44, 245, 43),
    color(109, 57, 24),
    color(233, 191, 152),
    color(174, 48, 40),
    color(251, 140, 119),
    color(160, 125, 98),
    color(70, 74, 21),
    color(202, 206, 1),
    color(247, 147, 2),
    color(255, 0, 135),
  ],
};

const discColorSet_36cols = {
  name: 'Default 36',
  colors: [
    color(90, 81, 86),
    color(228, 225, 227),
    color(246, 34, 46),
    color(254, 0, 250),
    color(22, 255, 50),
    color(50, 131, 254),
    color(254, 175, 22),
    color(176, 0, 104),
    color(28, 255, 206),
    color(144, 173, 28),
    color(46, 217, 255),
    color(222, 160, 253),
    color(170, 13, 254),
    color(248, 161, 159),
    color(50, 90, 155),
    color(196, 69, 28),
    color(28, 131, 86),
    color(133, 102, 13),
    color(177, 13, 161),
    color(251, 228, 38),
    color(28, 190, 79),
    color(250, 0, 135),
    color(252, 28, 191),
    color(247, 225, 160),
    color(192, 117, 166),
    color(120, 42, 182),
    color(170, 244, 0),
    color(189, 205, 255),
    color(130, 46, 28),
    color(181, 239, 181),
    color(126, 215, 209),
    color(28, 127, 147),
    color(216, 95, 247),
    color(104, 59, 121),
    color(102, 176, 255),
    color(59, 0, 251),
  ],
};

const discColorSet_50cols = {
  name: 'Default 50',
  colors: [
    color(38, 125, 255),
    color(65, 153, 23),
    color(124, 204, 61),
    color(153, 54, 23),
    color(144, 46, 153),
    color(255, 38, 246),
    color(160, 115, 255),
    color(153, 0, 55),
    color(115, 255, 238),
    color(61, 204, 204),
    color(23, 137, 153),
    color(204, 146, 92),
    color(173, 0, 255),
    color(0, 194, 255),
    color(153, 150, 69),
    color(92, 164, 204),
    color(204, 171, 0),
    color(182, 92, 204),
    color(255, 194, 38),
    color(241, 255, 77),
    color(31, 0, 153),
    color(38, 255, 73),
    color(255, 92, 0),
    color(153, 69, 129),
    color(101, 204, 92),
    color(169, 204, 31),
    color(0, 57, 204),
    color(69, 153, 103),
    color(104, 153, 0),
    color(255, 115, 148),
    color(204, 61, 78),
    color(193, 255, 115),
    color(0, 153, 116),
    color(46, 102, 153),
    color(69, 82, 153),
    color(0, 204, 57),
    color(46, 153, 50),
    color(255, 77, 184),
    color(77, 255, 169),
    color(96, 23, 153),
    color(124, 61, 204),
    color(44, 31, 204),
    color(204, 31, 114),
    color(153, 110, 46),
    color(255, 115, 115),
    color(204, 78, 61),
    color(31, 204, 142),
    color(204, 0, 171),
    color(51, 255, 0),
    color(77, 84, 255),
  ],
};

const seededRandomGenerator = getSimpleSeededRandomGenerator(0);
const largePaletteSize = 100;

const goldenAngle = 137.508;

const discColorLargeLight: { name: string; colors: Color[] } = {
  name: 'Large - light',
  colors: [],
};

const discColorLargeDark: { name: string; colors: Color[] } = {
  name: 'Large - Dark',
  colors: [],
};

for (let i = 0; i < largePaletteSize; i++) {
  discColorLargeLight.colors.push(
    hslColor(i * goldenAngle + 30, 50 + 50 * seededRandomGenerator(), 50 + 30 * seededRandomGenerator()),
  );
  discColorLargeDark.colors.push(
    hslColor(i * goldenAngle, 50 + 50 * seededRandomGenerator(), 20 + 60 * seededRandomGenerator()),
  );
}

function createNumericalColorSet(name: string, colorStops: Color[]): TpNumericalColorSet {
  const colorCount = 400;
  const colors = [];
  for (let colorNr = 0; colorNr < colorCount; colorNr++) {
    const colorFrac = colorNr / colorCount;
    const legIndex = Math.floor(colorFrac * (colorStops.length - 1));
    const legFrac = colorFrac * (colorStops.length - 1) - legIndex;
    const theColor = interpolateColors(colorStops[legIndex], colorStops[legIndex + 1], 1 - legFrac);
    colors.push(theColor);
  }
  return {
    name,
    colors,
  };
}

export function getColorFromNumericalSet(numColorSet: TpNumericalColorSet, fr: number) {
  const colorIndex = Math.round(fr * (numColorSet.colors.length - 1));
  return numColorSet.colors[Math.max(0, Math.min(numColorSet.colors.length - 1, colorIndex))];
}

export const discreteColorSets = [
  discColorSet1,
  discColorSet2,
  discColorSet3,
  discColorSet4,
  discColorSet_20cols,
  discColorSet_36cols,
  discColorSet_50cols,
  discColorLargeLight,
  discColorLargeDark,
];

export const numericalColorSets = [
  createNumericalColorSet('Default', [
    color(0, 70, 120),
    color(55, 90, 140),
    color(30, 140, 140),
    color(70, 190, 110),
    color(255, 200, 0),
  ]),
  createNumericalColorSet('Bright', [
    // color(0, 70, 120),
    color(55, 90, 140),
    color(30, 140, 140),
    color(70, 190, 110),
    color(255, 150, 0),
    color(255, 255, 100),
  ]),
  createNumericalColorSet('Bright (inverted)', [
    color(255, 255, 100),
    color(255, 150, 0),
    color(70, 190, 110),
    color(30, 140, 140),
    color(55, 90, 140),
  ]),
  createNumericalColorSet('Rainbow', [
    color(30, 30, 210),
    color(150, 200, 150),
    color(220, 220, 0),
    color(250, 160, 90),
    color(160, 0, 70),
  ]),
  createNumericalColorSet('Heat', [
    color(0, 0, 0),
    color(70, 0, 120),
    color(170, 30, 130),
    color(250, 130, 90),
    color(255, 160, 0),
  ]),
  createNumericalColorSet('Heat (inverted)', [
    color(255, 230, 210),
    color(250, 130, 90),
    color(170, 30, 130),
    color(70, 0, 120),
    color(0, 0, 0),
  ]),
  createNumericalColorSet('Heat (inverted, to white)', [
    color(70, 0, 120),
    color(170, 30, 130),
    color(250, 130, 90),
    color(255, 160, 0),
    color(240, 240, 240),
  ]),
  createNumericalColorSet('Gray scale', [color(240, 240, 240), color(0, 0, 0)]),
  createNumericalColorSet('Blue-Red', [color(0, 0, 192), color(0, 100, 255), color(220, 255, 220), color(192, 0, 0)]),
];

export function getDiscreteColorSet(colorSetName: string): TpDiscreteColorSet {
  for (const colorSet of discreteColorSets) if (colorSet.name == colorSetName) return colorSet;
  throw getUserError(`Invalid discrete color set name: ${colorSetName}`);
}

export function getNumericalColorSet(colorSetName: string): TpNumericalColorSet {
  for (const colorSet of numericalColorSets) if (colorSet.name == colorSetName) return colorSet;
  throw getUserError(`Invalid numerical color set name: ${colorSetName}`);
}
